import { Component, Inject, OnInit, HostListener } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef
} from "@angular/material/dialog";
import { PropertyService } from "../../../services/property.services";
import { Subject } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap
} from "rxjs/operators";
import { environment } from "../../../../../environments/environment";
import { MatSnackBar } from "@angular/material/snack-bar";
import { I18nService } from "../../../services";
import { MasterTenantsService } from "../../../services/master-tenants.service";
import { ConfirmAliasFieldsComponent } from "./confirm-alias-fields/confirm-alias-fields.component";
import { LangSelectorPipe } from "../../../pipes/lang-selector.pipe";
import { ConfirmationDialogComponent } from "../../confirmation-dialog/confirmation-dialog.component";
import { LocalNumberPipeSpot } from "../../../pipes/number-pipe";

@Component({
  selector: "app-edit-detail",
  templateUrl: "./edit-detail.component.html",
  styleUrls: ["./edit-detail.component.scss"]
})
export class EditDetailComponent implements OnInit {
  detailTenantForm: FormGroup;
  errorMessage = false;
  tenants: any;
  floorOrWarehouseControl: string;
  moduleOrSuiteControl: string;
  isIndustry;
  autocompleteResults = [];
  private searchSubject = new Subject<string>();
  isLoading = false;
  isDropdownOpen = false;
  dropdownClicked = false;
  currentCode: string;
  isCreate: any;
  isRetail;
  listOwners = [];
  autocompleteOwnersResults = [];
  private searchOwnerSubject = new Subject<string>();
  isDropdownOwnerOpen = false;
  dropdownOwnerClicked = false;
  isLoadingOwner = false;
  id;
  changeFlieds;
  listSpaceRetail = [];
  translatedTooltip;
  showCustomTooltip = false;
  idIndustry = null;
  corporation = null;
  listTenants;

  constructor(
    public dialogRef: MatDialogRef<EditDetailComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      data;
      buildingType;
      isAdmin;
      includedInQuarterlyStats;
      isCreate;
      industrialStatus;
      listTenants;
    },
    private fb: FormBuilder,
    private propertyService: PropertyService,
    private snackBar: MatSnackBar,
    private i18: I18nService,
    private masterTenantsService: MasterTenantsService,
    private dialog: MatDialog,
    private langSelectorPipe: LangSelectorPipe,
    private localNumberPipe: LocalNumberPipeSpot
  ) {
    this.currentCode = environment.platformId;
    this.isIndustry = this.data?.buildingType === 1001;
    this.isRetail = this.data?.buildingType === 3001;
    this.isCreate = this.data?.isCreate;
    this.id = this.data.industrialStatus.id;
    this.listTenants = data.listTenants;
    if (this.data?.data?.corporation) {
      this.corporation = this.data?.data?.corporation;
      this.data.data.tenantName = this.data?.data?.corporation?.name;
    }
    this.initDetailTenantForm();
  }

  ngOnInit(): void {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term: string) => {
          this.isLoading = true;
          if (this.currentCode == "mx" && this.isRetail) {
            return this.propertyService.fetchTenantsAutocompleteResultsRetial(
              term
            );
          }
          return this.propertyService.fetchTenantsAutocompleteResults(term);
        })
      )
      .subscribe((results: any[]) => {
        this.autocompleteResults = results;
        this.autocompleteResults.push(
          {
            tenantName: this.langSelectorPipe.transform(
              this.selectVacant("Vacant")
            )
          },
          {
            tenantName: this.langSelectorPipe.transform(
              this.selectVacant("Unknown")
            )
          }
        );
        this.isLoading = false;
      });
    if (this.data?.data) {
      this.detailTenantForm.patchValue(this.data?.data);
      this.onBlurArea();
      if (this.isIndustry) {
        this.onBlurModuleEfficiency();
        this.onBlurDocksPerModule();
      }
      if (!this.data.data.tenantName) {
        this.detailTenantForm
          .get("tenantName")
          .setValue(
            this.langSelectorPipe.transform(
              this.data.data.tenantNameOption.name
            )
          );

        this.searchSubject.next(this.data.data.tenantNameOption.name);
      } else {
        let tenantName = this.data.data.tenantName;
        if (this.data.data.corporation?.id > 0) {
          tenantName = this.data.data.corporation.name;
        }
        this.searchSubject.next(tenantName);
      }
    }

    if (
      this.currentCode == "br" ||
      (this.currentCode == "mx" && this.isCreate)
    ) {
      this.masterTenantsService.getCorporations().then((resp: any) => {
        this.listOwners = resp;

        this.searchOwnerSubject
          .pipe(
            debounceTime(300),
            distinctUntilChanged(),
            map((term: string) => {
              this.isLoadingOwner = true;
              return this.filterOwners(term);
            })
          )
          .subscribe((results: any[]) => {
            this.autocompleteOwnersResults = results;
            this.isLoadingOwner = false;
          });
      });
    }

    if (this.currentCode == "mx" && this.isRetail) {
      this.getSpaceReatilList();
    }
  }

  getSpaceReatilList() {
    this.masterTenantsService.getSpaceReatil().then((resp: any) => {
      this.listSpaceRetail = resp;
    });
  }

  selectVacant(tenantName) {
    switch (tenantName) {
      case "Vacant":
        return `{"pt-br":"Vago","en":"Vacant","es":"Disponible"}`;
      case "Unknown":
        return `{"pt-br":"Desconhecido","en":"Unknown","es":"Desconocido"}`;
    }
  }

  filterOwners(term: string): any[] {
    if (!term) {
      return [];
    }

    term = term.toLowerCase();

    return this.listOwners.filter(owner =>
      owner.name.toLowerCase().includes(term)
    );
  }

  initDetailTenantForm() {
    this.detailTenantForm = this.fb.group({
      ...(!this.isCreate ? { id: new FormControl() } : {}),
      ...(this.isIndustry
        ? {
            warehouse: new FormControl(
              this.isCreate
                ? null
                : { value: null, disabled: this.validateField() },
              [Validators.required]
            )
          }
        : {}),
      ...(this.isIndustry && !this.isCreate
        ? { warehouseAlias: new FormControl() }
        : {}),
      ...(!this.isIndustry && !this.isRetail
        ? {
            floor: new FormControl(
              this.isCreate
                ? null
                : { value: null, disabled: this.validateField() },
              [Validators.required]
            )
          }
        : {}),
      ...(!this.isIndustry && !this.isCreate
        ? { floorAlias: new FormControl() }
        : {}),
      ...(this.isIndustry
        ? {
            module: new FormControl(
              this.isCreate
                ? null
                : { value: null, disabled: this.validateField() },
              [Validators.required]
            )
          }
        : {}),
      ...(this.isIndustry && !this.isCreate
        ? { moduleAlias: new FormControl() }
        : {}),
      ...(!this.isIndustry
        ? {
            suiteNumber: new FormControl(
              this.isCreate
                ? null
                : { value: null, disabled: this.validateField() },
              [Validators.required]
            )
          }
        : {}),
      ...(!this.isIndustry && !this.isCreate
        ? { suiteNumberAlias: new FormControl() }
        : {}),
      area: new FormControl({
        value: null,
        disabled: this.validateField()
      }),
      ...(!this.isIndustry && this.currentCode == "br"
        ? {
            bomaArea: new FormControl({
              value: null,
              disabled: this.validateField()
            })
          }
        : {}),
      ...(this.isIndustry ? { moduleEfficiency: new FormControl() } : {}),
      ...(this.isIndustry ? { docksPerModule: new FormControl() } : {}),
      tenantName: new FormControl({
        value: null,
        disabled: this.validateField()
      }),
      owners: new FormControl(),
      ownersGroup: new FormControl(),
      ownersContactName: new FormControl(),
      ownersContactEmail: new FormControl(null, Validators.email),
      ownersContactPhone: new FormControl(),

      ...(this.isRetail && this.currentCode == "mx"
        ? {
            spaceTypeRetail: new FormControl({
              value: null
            })
          }
        : {})
    });
  }

  save() {
    if (this.detailTenantForm.valid) {
      const duplicateFlooSuite = this.isDuplicateFloorSuite(
        this.detailTenantForm.value,
        this.data.listTenants
      );

      const duplicateWarehouseModule = this.isDuplicateWarehouseModule(
        this.detailTenantForm.value,
        this.data.listTenants
      );

      if (this.validateErrorTenantName()) {
        return;
      }
      if (!this.isCreate && this.validateChangesAliasField()) {
        this.confirmSaveAliasFields();
      } else {
        if (duplicateFlooSuite || duplicateWarehouseModule) {
          this.showMessage(
            this.i18.get(
              duplicateFlooSuite
                ? "detail-tenant.messageDuplicateFloor"
                : "detail-tenant.messageDuplicateWarehouseModule"
            )
          );
        } else {
          const rawForm = this.detailTenantForm.getRawValue();
          const cleanedForm = {
            ...rawForm,
            area: this.convertStringToNumber(rawForm.area),
            moduleEfficiency: this.convertStringToNumber(
              rawForm.moduleEfficiency
            ),
            docksPerModule: this.convertStringToNumber(rawForm.docksPerModule)
          };
          if (this.corporation) {
            cleanedForm["corporation"] = this.corporation;
            cleanedForm["tenantName"] = this.corporation.name;
          }

          this.dialogRef.close({
            updatedTenant: cleanedForm,
            idIndustry: this.idIndustry
          });
        }
      }
    } else {
      if (this.detailTenantForm.get("ownersContactEmail").invalid) {
        this.showMessage(this.i18.get("detail-tenant.messageEmail"));
      } else {
        if (this.isIndustry) {
          this.showMessage(this.i18.get("detail-tenant.messageIndustrial"));
        } else {
          this.showMessage(this.i18.get("detail-tenant.messageOffice"));
        }
      }
    }
  }

  isDuplicateFloorSuite(form: any, list: any[]): boolean {
    const formFloor = form.floor?.trim();
    const formSuite = form.suiteNumber?.trim();

    if (!formFloor || !formSuite) {
      return false;
    }

    const normalizedFormFloor = formFloor.toLowerCase();
    const normalizedFormSuite = formSuite.toLowerCase();

    return list.some(item => {
      const itemFloor = item.floor?.trim();
      const itemSuite = item.suiteNumber?.trim();

      if (!itemFloor || !itemSuite) return false;

      return (
        itemFloor.toLowerCase() === normalizedFormFloor &&
        itemSuite.toLowerCase() === normalizedFormSuite &&
        form?.id != item?.id
      );
    });
  }

  isDuplicateWarehouseModule(form: any, list: any[]): boolean {
    const formWarehouse = form.warehouse?.trim();
    const formModule = form.module?.trim();

    if (!formWarehouse || !formModule) {
      return false;
    }

    const normalizedFormWarehouse = formWarehouse.toLowerCase();
    const normalizedFormModule = formModule.toLowerCase();

    return list.some(item => {
      const itemWarehouse = item.warehouse?.trim();
      const itemModule = item.module?.trim();

      if (!itemWarehouse || !itemModule) return false;

      return (
        itemWarehouse.toLowerCase() === normalizedFormWarehouse &&
        itemModule.toLowerCase() === normalizedFormModule &&
        form?.id != item?.id
      );
    });
  }

  validateErrorTenantName() {
    const tenantName = this.detailTenantForm.get("tenantName").value;
    const validTenantName = this.autocompleteResults.filter(
      item => item.tenantName == tenantName
    );
    return validTenantName.length == 0;
  }

  close() {
    this.dialogRef.close();
  }

  onInputChange(event: Event): void {
    const input = (event.target as HTMLInputElement).value;
    this.searchSubject.next(input);
    this.isDropdownOpen = true;
  }

  onInputChangeOwners(event: Event): void {
    const input = (event.target as HTMLInputElement).value;
    this.searchOwnerSubject.next(input);
    this.isDropdownOpen = true;
  }

  onSelectOption(option: any): void {
    this.detailTenantForm.get("tenantName")?.setValue(option.tenantName);
    this.idIndustry = option.industryId;
    if (option.corporationId > 0) {
      this.corporation = { id: option.corporationId, name: option.tenantName };
    }
    this.isDropdownOpen = false;
  }

  onSelectOwnerOption(option: any): void {
    this.detailTenantForm.get("owners")?.setValue(option.name);
    this.autocompleteOwnersResults = [];
    this.isDropdownOwnerOpen = false;
  }

  @HostListener("document:click", ["$event"])
  onClickOutside(event: MouseEvent) {
    const clickedInside =
      (event.target as HTMLElement).closest(".tenant-input") ||
      (event.target as HTMLElement).closest(".autocomplete-dropdown");
    if (!clickedInside) {
      this.isDropdownOpen = false;
    }
  }

  showMessage(message) {
    this.snackBar.open(this.i18.get(message), "Close", {
      duration: 3000,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: ["custom-snackbar"]
    });
  }

  confirmSaveAliasFields() {
    const dialogRef = this.dialog.open(ConfirmAliasFieldsComponent, {
      width: "400px",
      panelClass: "alias-fields",
      data: {
        changeFlieds: this.changeFlieds
      }
    });

    dialogRef.afterClosed().subscribe((confirm: any) => {
      if (confirm) {
        const rawForm = this.detailTenantForm.value;
        const rawFormValues = this.detailTenantForm.getRawValue();

        const duplicateValues = this.listTenants.filter(
          item => item.id !== rawForm.id
        );

        const hasDuplicateFloorSuite = duplicateValues.some(item => {
          const floorsMatch =
            (rawForm.floor && item.floor && rawForm.floor === item.floor) ||
            (rawForm.floor &&
              item.floorAlias &&
              rawForm.floor === item.floorAlias) ||
            (rawForm.floorAlias &&
              item.floor &&
              rawForm.floorAlias === item.floor) ||
            (rawForm.floorAlias &&
              item.floorAlias &&
              rawForm.floorAlias === item.floorAlias);

          const suitesMatch =
            (rawForm.suiteNumber &&
              item.suiteNumber &&
              rawForm.suiteNumber === item.suiteNumber) ||
            (rawForm.suiteNumber &&
              item.suiteNumberAlias &&
              rawForm.suiteNumber === item.suiteNumberAlias) ||
            (rawForm.suiteNumberAlias &&
              item.suiteNumber &&
              rawForm.suiteNumberAlias === item.suiteNumber) ||
            (rawForm.suiteNumberAlias &&
              item.suiteNumberAlias &&
              rawForm.suiteNumberAlias === item.suiteNumberAlias);

          return floorsMatch && suitesMatch;
        });

        const hasDuplicateModuleWarehouse = duplicateValues.some(item => {
          const modulesMatch =
            (rawForm.module && item.module && rawForm.module === item.module) ||
            (rawForm.module &&
              item.moduleAlias &&
              rawForm.module === item.moduleAlias) ||
            (rawForm.moduleAlias &&
              item.module &&
              rawForm.moduleAlias === item.module) ||
            (rawForm.moduleAlias &&
              item.moduleAlias &&
              rawForm.moduleAlias === item.moduleAlias);

          const warehousesMatch =
            (rawForm.warehouse &&
              item.warehouse &&
              rawForm.warehouse === item.warehouse) ||
            (rawForm.warehouse &&
              item.warehouseAlias &&
              rawForm.warehouse === item.warehouseAlias) ||
            (rawForm.warehouseAlias &&
              item.warehouse &&
              rawForm.warehouseAlias === item.warehouse) ||
            (rawForm.warehouseAlias &&
              item.warehouseAlias &&
              rawForm.warehouseAlias === item.warehouseAlias);

          return modulesMatch && warehousesMatch;
        });

        const hasDuplicateShop =
          this.isRetail &&
          duplicateValues.some(
            item =>
              rawForm.suiteNumber &&
              (rawForm.suiteNumber === item.suiteNumber ||
                rawForm.suiteNumber === item.suiteNumberAlias)
          );
        if (
          hasDuplicateFloorSuite ||
          hasDuplicateModuleWarehouse ||
          hasDuplicateShop
        ) {
          if (hasDuplicateFloorSuite) {
            this.showMessage(
              this.i18.get("detail-tenant.messageDuplicateFloor")
            );
          } else if (hasDuplicateModuleWarehouse) {
            this.showMessage(
              this.i18.get("detail-tenant.messageDuplicateModule")
            );
          } else if (hasDuplicateShop) {
            this.showMessage(
              this.i18.get("detail-tenant.messageDuplicateSpaceType")
            );
          }
          return;
        }
        const cleanedForm = {
          ...rawFormValues,
          area: this.convertStringToNumber(rawFormValues.area),
          moduleEfficiency: this.convertStringToNumber(
            rawFormValues.moduleEfficiency
          ),
          docksPerModule: this.convertStringToNumber(
            rawFormValues.docksPerModule
          )
        };

        if (this.corporation) {
          cleanedForm["corporation"] = this.corporation;
          cleanedForm["tenantName"] = this.corporation.name;
        }

        this.dialogRef.close({
          updatedTenant: cleanedForm,
          idIndustry: this.idIndustry
        });
      }

      if (confirm == false) {
        this.changeFlieds.forEach(field => {
          const controlAlias = field.includes("Alias")
            ? this.detailTenantForm.get(field)
            : null;
          if (controlAlias) {
            controlAlias.reset();
          }
          const control = !field.includes("Alias")
            ? this.detailTenantForm.get(field)
            : null;
          if (control) {
            control.setValue(this.data.data[field]);
          }
        });
      }
    });
  }

  validateField() {
    return (
      this.data.includedInQuarterlyStats && (this.id == 1 || this.id == 1001)
    );
  }

  validateChangesAliasField() {
    const current = this.detailTenantForm.value;

    const fieldsToCheck = [
      "floor",
      "suiteNumber",
      "warehouse",
      "module",
      "floorAlias",
      "suiteNumberAlias",
      "warehouseAlias",
      "moduleAlias"
    ] as const;

    this.changeFlieds = fieldsToCheck.flatMap(key => {
      const currentValue = current[key];
      const originalValue = this.data?.data[key];

      if (currentValue != null && currentValue !== originalValue) {
        if (this.isRetail && key === "suiteNumber") return ["shop"];
        if (this.isRetail && key === "suiteNumberAlias") return ["shopAlias"];
        return [key];
      }

      return [];
    });

    return this.changeFlieds.length > 0;
  }

  delete(): void {
    this.dialog.open(ConfirmationDialogComponent, {
      width: "400px",
      disableClose: true,
      autoFocus: true,
      panelClass: ["confirmation-dialog-custom-delete"],
      data: {
        title: this.i18.get("detail-tenant.confirmDelete"),
        message: this.i18.get("detail-tenant.confirmDeleteMessage"),
        confirmLabel: this.i18.get("detail.yes"),
        cancelLabel: this.i18.get("detail.no"),
        onConfirm: () => {
          this.dialogRef.close({ deleted: true });
        }
      }
    });
  }

  onInputChangeArea(value: string) {
    const numeric = value.toString().replace(/[^\d.-]/g, "");
    const raw = parseFloat(numeric);
    this.detailTenantForm.get("area")?.setValue(raw);
  }

  formatNumber(value: string | number): string {
    return this.localNumberPipe.transform(value, ".2-2");
  }

  onBlurArea() {
    let value = this.detailTenantForm.get("area")!.value;
    value = this.convertStringToNumber(value);
    const formatted = this.formatNumber(value);
    this.detailTenantForm
      .get("area")!
      .setValue(formatted, { emitEvent: false });
  }

  onInputArea(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleanValue = input.value.replace(/[^0-9.,-]/g, "");

    this.detailTenantForm
      .get("area")!
      .setValue(cleanValue, { emitEvent: false });
  }

  onBlurModuleEfficiency() {
    let value = this.detailTenantForm.get("moduleEfficiency")!.value;
    value = this.convertStringToNumber(value);
    const formatted = this.formatNumber(value);
    this.detailTenantForm
      .get("moduleEfficiency")!
      .setValue(formatted, { emitEvent: false });
  }

  onInputModuleEfficiency(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleanValue = input.value.replace(/[^0-9.,-]/g, "");

    this.detailTenantForm
      .get("moduleEfficiency")!
      .setValue(cleanValue, { emitEvent: false });
  }

  onBlurDocksPerModule() {
    let value = this.detailTenantForm.get("docksPerModule")!.value;
    value = this.convertStringToNumber(value);
    const formatted = this.formatNumber(value);
    this.detailTenantForm
      .get("docksPerModule")!
      .setValue(formatted, { emitEvent: false });
  }

  onInputDocksPerModule(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleanValue = input.value.replace(/[^0-9.,-]/g, "");

    this.detailTenantForm
      .get("docksPerModule")!
      .setValue(cleanValue, { emitEvent: false });
  }

  convertStringToNumber(value: any): number | null {
    if (typeof value === "number") return value;
    if (typeof value !== "string") return null;

    let v = value.trim().replace(/\s+/g, "");
    if (!v) return null;

    const hasComma = v.includes(",");
    const hasDot = v.includes(".");

    if (hasComma && hasDot) {
      const lastComma = v.lastIndexOf(",");
      const lastDot = v.lastIndexOf(".");
      if (lastComma > lastDot) {
        v = v.replace(/\./g, "").replace(",", ".");
      } else {
        v = v.replace(/,/g, "");
      }
    } else if (hasComma) {
      if (/^\d{1,3}(,\d{3})+$/.test(v)) {
        v = v.replace(/,/g, "");
      } else {
        v = v.replace(",", ".");
      }
    } else if (hasDot) {
      if (/^\d{1,3}(\.\d{3})+$/.test(v)) {
        v = v.replace(/\./g, "");
      }
    }

    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }

  blockZero(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;

    const allowedKeys = [
      "Backspace",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Delete"
    ];
    if (allowedKeys.includes(event.key)) return;

    const currentValue = input.value;
    const pressedKey = event.key;

    if (pressedKey === "0" && currentValue === "") {
      event.preventDefault();
    }
  }

  delayedHideTooltip() {
    setTimeout(() => {
      this.showCustomTooltip = false;
    }, 900);
  }

  isAreaInvalid(): boolean {
    const area = this.convertStringToNumber(
      this.detailTenantForm.get("area")!.value
    );
    return !area || area === 0;
  }

  validateMessage() {
    if (this.validateErrorTenantName()) {
      this.translatedTooltip = this.i18.get("detail-tenant.messageErrorName");
      return true;
    }
    if (this.isAreaInvalid()) {
      this.translatedTooltip = this.i18.get("detail-tenant.messageArea");
      return true;
    }
  }
}
